import { ButtonHTMLAttributes, forwardRef } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      fullWidth = false,
      className = '',
      disabled,
      ...props
    },
    ref
  ): JSX.Element => {
    const baseStyles =
      'inline-flex items-center justify-center font-semibold rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98] hover:brightness-110 active:brightness-90';

    const variants = {
      primary:
        'bg-site-accent hover:bg-site-accent/95 active:bg-site-accent/80 text-white shadow-lg hover:shadow-xl active:shadow-md focus:ring-site-accent backdrop-blur-sm',
      secondary:
        'bg-site-secondary/90 backdrop-blur-sm hover:bg-site-secondary/95 active:bg-site-secondary/80 text-white shadow-lg hover:shadow-xl active:shadow-md focus:ring-site-secondary',
      outline:
        'border-2 border-site-accent/50 text-site-accent hover:bg-site-accent/15 active:bg-site-accent/25 hover:border-site-accent/70 active:border-site-accent dark:hover:bg-site-accent/20 dark:active:bg-site-accent/30 focus:ring-site-accent backdrop-blur-md',
      ghost:
        'text-site-text hover:bg-gray-100/70 active:bg-gray-200/70 dark:text-gray-300 dark:hover:bg-gray-800/70 dark:active:bg-gray-700/70 focus:ring-gray-500 backdrop-blur-sm',
      danger:
        'bg-theme-error hover:bg-theme-error/95 active:bg-theme-error/80 text-white shadow-lg hover:shadow-xl active:shadow-md focus:ring-theme-error backdrop-blur-sm',
    };

    const sizes = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-6 py-3 text-lg',
    };

    const widthClass = fullWidth ? 'w-full' : '';

    return (
      <button
        ref={ref}
        className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${widthClass} ${className}`}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading...
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

